import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { ActivityIcon, XIcon } from 'lucide-react';
import { navGroups } from '../../data/navigation';
import { cn } from '../../lib/cn';
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}
const badgeTones: Record<string, string> = {
  danger: 'bg-red-500 text-white',
  warning: 'bg-amber-500 text-white',
  brand: 'bg-brand-600 text-white'
};
export function MobileMenu({
  open,
  onClose
}: MobileMenuProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);
  return <AnimatePresence data-id="element-331" data-mp="54wrec2xqfe8">
      {open && <>
          <motion.button type="button" aria-label="Close main menu" className="fixed inset-0 z-40 bg-slate-950/45 md:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18
      }} onClick={onClose} data-id="element-332" data-mp="53bzku809ak2" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Main menu" className="fixed inset-y-0 left-0 z-50 flex w-[min(20rem,calc(100vw-2.5rem))] flex-col border-r border-slate-800 bg-slate-900 text-slate-300 shadow-2xl md:hidden" initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'tween',
        duration: 0.22,
        ease: 'easeOut'
      }} data-id="element-333" data-mp="3ib6rksrok8q3">
            <div className="flex h-14 items-center gap-2.5 border-b border-slate-800 px-4" data-id="element-334" data-mp="32p0zzooog0p6">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white" data-id="element-335" data-mp="2yo8oeosc1jpq">
                <ActivityIcon className="h-5 w-5" data-id="element-336" data-mp="3co00ul37as1m" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-337" data-mp="37onb9sky36vc">
                <p className="truncate font-display text-sm font-bold leading-tight text-white" data-id="element-338" data-mp="37pyf1see4emg">Creative Energy - EHR</p>
                <p className="truncate text-[10px] leading-tight text-brand-200" data-id="element-339" data-mp="2jzc864g4h0e1">Integrated Healthcare Enterprise</p>
              </div>
              <button ref={closeButtonRef} type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" aria-label="Close main menu" data-id="element-340" data-mp="2tom27puj0t4s">
                <XIcon className="h-5 w-5" data-id="element-341" data-mp="br7dbxm1nrto" />
              </button>
            </div>

            <nav aria-label="Main navigation" className="flex-1 space-y-4 overflow-y-auto px-2.5 py-3" data-id="element-342" data-mp="ghw8po0uh8y3">
              {navGroups.map(group => <div key={group.title} data-id="element-343" data-mp="2sc5kh3cq9o9">
                  <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-344" data-mp="6s36veqi8871">
                    {group.title}
                  </p>
                  <div className="space-y-0.5" data-id="element-345" data-mp="ufui0f0b6f6q">
                    {group.items.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/' || item.to === '/finance'} onClick={onClose} className={({
                isActive
              }) => cn('relative flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400', isActive ? 'bg-brand-600/15 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white')} data-id="element-346" data-mp="zexo82s0yrga">
                        {({
                  isActive
                }) => <>
                            {isActive && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-brand-500" data-id="element-347" data-mp="d8ms0ztd0q1k" />}
                            <item.icon className="h-[18px] w-[18px] shrink-0" data-id="element-348" data-mp="8be92x0e0hz9" />
                            <span className="flex-1 truncate" data-id="element-349" data-mp="47on8fb3kehx">{item.label}</span>
                            {item.badge && <span className={cn('rounded-full px-1.5 py-px text-[10px] font-bold', badgeTones[item.badgeTone || 'brand'])} data-id="element-350" data-mp="3vheyp0yu604c">
                                {item.badge}
                              </span>}
                          </>}
                      </NavLink>)}
                  </div>
                </div>)}
            </nav>

            <div className="border-t border-slate-800 px-4 py-3 text-xs text-slate-500" data-id="element-351" data-mp="18j1ex1kc34xr">
              Main Campus · Clinical workspace
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}